/**
 * Defence 2 and defence 3 while they are running: which batch has gone, what is
 * holding the next one, when it may release, and what the messages already sent
 * are doing.
 *
 * ## Three states that must never be drawn as each other
 *
 * A campaign between batches, a campaign that stopped itself, and a campaign
 * whose delivery reports have not arrived look identical if you only draw the
 * counts. They are the three states this panel exists to keep apart:
 *
 *   * **holding** — the plan working. `holding_between_waves` releases itself,
 *     and `HOLDING_BETWEEN_WAVES_MEANING` on the header card already says so; the
 *     hold sentence here says which of the gate's conditions is the one still
 *     open.
 *   * **holding for good** — the same status, and *not* the plan working. A
 *     segment re-materialised underneath a scheduled campaign leaves more
 *     recipients than its committed waves cover, so the last wave clears and
 *     there is nothing left to release. This panel used to title every hold
 *     *"Waiting before the next batch"* in `info`, which on that state is a
 *     reassurance about a batch that does not exist. The heading and the tone now
 *     come from `WAVE_HOLD_HEADING`, keyed on the hold's own reason, so the two
 *     reasons that need a person — `plan_exhausted` and `unreadable` — are drawn
 *     as stops rather than as waits.
 *   * **halted** — `CampaignHaltPanel`, a separate component with an action,
 *     because a person has to decide.
 *   * **nothing reported yet** — a batch that finished five minutes ago with no
 *     events back is not a clean batch, it is an unanswered one, and it gets
 *     `WAVE_HOLD_SENTENCES.no_delivery_reports_yet` rather than a rate of zero.
 *     After three hours the wording escalates to name the likely cause and the
 *     verdict does not change, which is `waveHoldReading` following
 *     `mayWaveRelease` exactly.
 *
 * ## The hold explanation is read, not decided
 *
 * `waveHoldReading` in `list-safety-display.ts` explains a hold the API has
 * already taken; it does not evaluate the gate, and it says why in its own
 * header. Nothing on this screen releases anything, and the "next batch no
 * earlier than" line is deliberately worded as a floor rather than a promise:
 * the clock is only half the gate, and the delivery reports are the half a clock
 * cannot predict.
 *
 * ## The risk figure appears only once there is something to measure
 *
 * `campaign.progress.startedAt` gates it. Before the first message is handed to
 * a provider there is no denominator, and a panel that printed "no results yet"
 * on a campaign scheduled for next Tuesday would be reporting an absence as a
 * finding. Once sending has begun, a `null` reading means the rate genuinely
 * could not be computed — which is a fact worth stating and is never rendered as
 * zero.
 *
 * Every word beside that figure comes from `sendRiskCountSentence`, which takes a
 * `SendRiskGate` rather than a rate and a noun, so this file cannot put "bounced"
 * next to a WhatsApp number or Amazon's threshold next to Meta's.
 *
 * ## The wave rows show what happened, not what was planned
 *
 * `releasedCount` is on the `sent` and `cleared` arms and `plannedSize` is on all
 * four, and they are shown as two different numbers where they differ: a batch
 * planned for 408 that actually attempted 402 is a fact somebody will want after
 * a bad send, and averaging them into one figure loses the only interesting half.
 */
import {
  CAMPAIGN_WAVE_STATUS_LABELS,
  isCampaignTerminal,
  sendRiskGateFor,
  type Campaign,
  type CampaignWave,
  type RateReading,
  type WarmUpPlan,
} from '@wend/shared';
import { Badge, Callout, Kicker } from '@wend/ui';

import { formatInTimeZone } from '../../lib/campaign-display';
import { formatCount, pluralise } from '../../lib/format';
import {
  CAMPAIGN_WAVE_STATUS_TONE,
  WAVE_HOLD_HEADING,
  formatHoldWait,
  releasingWave,
  sendRiskCountSentence,
  waveHoldReading,
} from '../../lib/list-safety-display';
import { WarmUpPlanPanel } from './WarmUpPlanPanel';

export interface CampaignWavesPanelProps {
  campaign: Campaign;
  /** Null until the campaign has been scheduled and its wave rows written. */
  waves: readonly CampaignWave[] | null;
  /** Null when the list was below the floor and got no plan at all. */
  plan: WarmUpPlan | null;
  /** Null when the rate could not be computed. Never a stand-in for zero. */
  reading: RateReading | null;
  /**
   * What `plan` was sized from — `listSafety.willAttempt`. Passed through to
   * {@link WarmUpPlanPanel}, which needs it to say whether "no batches" means a
   * small list or a filtered one.
   */
  willAttempt: number;
  /** `listSafety.warmUpEnforced`, forwarded so the no-plan sentence is honest. */
  warmUpEnforced: boolean;
  /** Everybody this channel can reach — `health.signals.total`. The other half of that. */
  reachable: number;
}

export function CampaignWavesPanel({
  campaign,
  waves,
  plan,
  reading,
  willAttempt,
  warmUpEnforced,
  reachable,
}: CampaignWavesPanelProps) {
  const hold = waveHoldReading({
    campaignStatus: campaign.status,
    waves,
    reading,
    channel: campaign.channel,
  });

  // The heading and tone come from the hold's own reason, never from a literal:
  // two of the reasons are not waits at all, and titling them "Waiting before
  // the next batch" is the reassuring half of a state that needs a person.
  const holdHeading = WAVE_HOLD_HEADING[hold.reason];

  const risk =
    campaign.progress.startedAt === null
      ? null
      : sendRiskCountSentence(sendRiskGateFor(campaign.channel), reading);

  // No rows yet: the plan is all there is to show, and `WarmUpPlanPanel` already
  // distinguishes "no plan needed" from "a plan with no batches".
  if (waves === null || waves.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        <WarmUpPlanPanel
          plan={plan}
          channel={campaign.channel}
          willAttempt={willAttempt}
          reachable={reachable}
          warmUpEnforced={warmUpEnforced}
        />
        {/* The tone follows the reason. A campaign that says it is holding
            while its wave rows cannot be read is the fail-closed branch, not the
            ordinary wait, and the table says so rather than this call site. */}
        {campaign.status === 'holding_between_waves' && (
          <Callout tone={holdHeading.tone} title={holdHeading.title}>
            {hold.message}
          </Callout>
        )}
        {risk !== null && (
          <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">{risk}</p>
        )}
      </div>
    );
  }

  const ordered = [...waves].sort((a, b) => a.waveNumber - b.waveNumber);
  /*
   * A FINISHED CAMPAIGN HAS NO BATCH STILL WAITING, and the count used to say it
   * did.
   *
   * `cleared` is written when the **next** wave is claimed — it is the record
   * that a gate opened, not a fact about time — so the last wave of every
   * campaign is `sent` for ever: there is no next wave to open a gate for it.
   * Counting only `cleared` therefore rendered "2 of 3 batches done" under a
   * `Sent` badge and a description saying every recipient had been resolved,
   * with the last batch reading "Sent, waiting for results". A sender reads that
   * as 800 messages in limbo.
   *
   * The repair is here rather than in the database, because `cleared` would be a
   * lie there: nothing cleared that wave, and its `cleared_at` would have to be
   * invented. What is true is that the campaign is over, so a wave that was sent
   * is finished. Pending waves on a `failed` or `cancelled` campaign are not
   * counted — they never ran, and saying they did would be the opposite error.
   */
  const finished = isCampaignTerminal(campaign.status);
  const done = ordered.filter(
    (wave) => wave.status === 'cleared' || (finished && wave.status === 'sent'),
  ).length;
  const running = releasingWave(ordered);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Kicker as="span">
          Sent in batches
        </Kicker>
        <Badge tone="neutral">
          {formatCount(done)} of {pluralise(ordered.length, 'batch', 'batches')} done
        </Badge>
        {/* Which one is going out, said as a fact rather than left to be read
            off a badge halfway down the list. */}
        {running !== null && <Badge tone="info">Batch {String(running.waveNumber)} sending now</Badge>}
      </div>

      <ol className="flex flex-col">
        {ordered.map((wave) => (
          <li
            key={wave.waveNumber}
            className="flex flex-col gap-1.5 border-t border-line py-3 first:border-t-0 first:pt-0"
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                Batch {String(wave.waveNumber)}
              </span>
              <span className="text-[1.05rem] font-semibold tabular-nums text-text">
                {formatCount(
                  wave.status === 'sent' || wave.status === 'cleared'
                    ? wave.releasedCount
                    : wave.plannedSize,
                )}
              </span>
              <Badge tone={CAMPAIGN_WAVE_STATUS_TONE[wave.status]}>
                {CAMPAIGN_WAVE_STATUS_LABELS[wave.status]}
              </Badge>
            </div>

            <p className="text-[0.82rem] leading-relaxed text-muted">
              {waveLine(wave, campaign.timezone, finished)}
            </p>
          </li>
        ))}
      </ol>

      {campaign.status === 'holding_between_waves' && (
        <Callout tone={holdHeading.tone} title={holdHeading.title}>
          <span className="block">{hold.message}</span>
          {hold.holdUntil !== null && (
            <span className="mt-1.5 block text-muted">
              The next batch can start no earlier than{' '}
              {formatInTimeZone(hold.holdUntil, campaign.timezone)}, and only once enough delivery
              reports have come back.
            </span>
          )}
        </Callout>
      )}

      {risk !== null && (
        <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">{risk}</p>
      )}
    </div>
  );
}

/**
 * One wave's own line: how many were planned against how many were attempted,
 * and the times that matter for the state it is in.
 *
 * The planned figure is repeated beside the attempted one **only when they
 * differ**, because on the ordinary batch they are the same number and printing
 * it twice makes the exception invisible.
 */
function waveLine(wave: CampaignWave, timezone: string, campaignFinished: boolean): string {
  switch (wave.status) {
    case 'pending':
      return `${pluralise(wave.plannedSize, 'recipient')} planned, waiting for the batch before it.`;
    case 'releasing':
      return `${pluralise(wave.plannedSize, 'recipient')} planned. Started ${formatInTimeZone(wave.releasedAt, timezone)}.`;
    case 'sent': {
      const planned =
        wave.releasedCount === wave.plannedSize ? '' : ` of ${formatCount(wave.plannedSize)} planned`;
      const attempted = `${pluralise(wave.releasedCount, 'recipient')}${planned} attempted, finishing ${formatInTimeZone(wave.sentAt, timezone)}.`;
      /*
       * The wait is a fact about what happens next, and on a finished campaign
       * nothing happens next — no gate will open for this wave and no batch is
       * waiting on its results. Printing it there is the half of the "2 of 3
       * batches done" defect a reader actually sees.
       */
      return campaignFinished
        ? attempted
        : `${attempted} Waiting ${formatHoldWait(wave.holdMinutes)} from then for results.`;
    }
    case 'cleared': {
      const planned =
        wave.releasedCount === wave.plannedSize ? '' : ` of ${formatCount(wave.plannedSize)} planned`;
      return `${pluralise(wave.releasedCount, 'recipient')}${planned} attempted, cleared ${formatInTimeZone(wave.clearedAt, timezone)}.`;
    }
    default: {
      // The union is closed by `campaignWaveSchema`; this is the branch that
      // turns a fifth member into a compile error rather than a blank line.
      const exhaustive: never = wave;
      return String(exhaustive);
    }
  }
}
